import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Viewer, EquirectangularAdapter } from '@photo-sphere-viewer/core';
import { VirtualTourPlugin } from '@photo-sphere-viewer/virtual-tour-plugin';
import '@photo-sphere-viewer/core/index.css';
import '@photo-sphere-viewer/virtual-tour-plugin/index.css';
import { LoadingOutlined } from '@ant-design/icons';
import { Button, Empty } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPlus, faMinus, faExpand } from '@fortawesome/free-solid-svg-icons';
import GeneralHelper from '../../helpers/general';
import API from '../../helpers/api';
import Minimap from './components/Minimap';
import './StreetViewPage.css';

const StreetViewPage = ({ getPanoramaNodeById, pageProps }) => {
	const history = useHistory();
	const location = useLocation();
	// Read once at mount: moving between panoramas rewrites this entry (see `handleNodeChanged`),
	// and what matters is how the page was entered, not what the URL says now.
	const openedFromMap = useRef(location.state?.fromMap === true);
	const viewerContainer = useRef(null);
	const viewer = useRef(null);
	const virtualTourPlugin = useRef(null);
	const initialLoadRef = useRef(true);
	const query = GeneralHelper.getParamsFromUrl();
	const { id } = query || {};

	const { panorama_node, loading } = pageProps || {};
	const [allNodesData, setAllNodesData] = useState(null);
	const [isLoadingData, setIsLoadingData] = useState(true);
	const [viewerYaw, setViewerYaw] = useState(0);
	const [currentNodeId, setCurrentNodeId] = useState(null);
	const [requested, setRequested] = useState(false);

	// Only fetch initial panorama node once
	useEffect(() => {
		if (id && initialLoadRef.current) {
			getPanoramaNodeById({ id });
			initialLoadRef.current = false;
			setRequested(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	// A shared 360 link can outlive the panorama it points at — the node may since have been
	// hidden from the app or deleted — and /street-view is reachable with no id at all. Both used
	// to be someone else's problem while the page was debug-only; now they have to end in a way
	// out rather than on a spinner that never resolves.
	const nodeUnavailable = !id || (requested && !loading && !panorama_node);

	// Update URL with building info when panorama_node is loaded
	useEffect(() => {
		if (panorama_node && panorama_node.building_id && panorama_node.building_floor_id) {
			const currentQuery = GeneralHelper.getParamsFromUrl();
			// Only update if building_id or building_floor_id is missing
			if (!currentQuery.building_id || !currentQuery.building_floor_id) {
				const queryString = GeneralHelper.objectToQueryString({
					id: panorama_node._id,
					building_id: panorama_node.building_id,
					building_floor_id: panorama_node.building_floor_id
				});
				// Keep `state` — it records that the map is one step back in the history stack, and a
				// bare string replace would drop it (and with it the back button's destination
				// across a reload).
				history.replace({ pathname: '/street-view', search: `?${queryString}`, state: history.location.state });
			}
		}
	}, [panorama_node, history]);

	// Fetch ALL nodes and edges once when panorama_node is loaded
	useEffect(() => {
		if (!panorama_node || allNodesData) return;

		const fetchAllData = async () => {
			setIsLoadingData(true);
			try {
				const [nodesResponse, edgesResponse, geojsonResponse] = await Promise.all([
					API.panorama_nodes.getPanoramaNodes({
						building_floor_id: panorama_node.building_floor_id
					}),
					API.panorama_edges.getPanoramaEdges({
						building_floor_id: panorama_node.building_floor_id
					}),
					API.building_floors.getBuildingFloorGeojson({
						building_floor_id: panorama_node.building_floor_id
					})
				]);

				if (nodesResponse.meta.code === 200 && edgesResponse.meta.code === 200) {
					const nodes = nodesResponse.data.panorama_nodes || [];
					const edges = edgesResponse.data.panorama_edges || [];
					const geojson = geojsonResponse?.data?.building_floor?.geojson || null;

					const nodesWithAssets = nodesResponse.data.panorama_nodes.map(node => {
						if (node.panorama_url) return node;
						else return null;
					});

					const validNodes = nodesWithAssets.filter(node => node !== null);
					setAllNodesData({
						nodes: validNodes,
						edges: edges,
						geojson: geojson,
						nodesMap: validNodes.reduce((acc, node) => {
							acc[node._id] = node;
							return acc;
						}, {})
					});
					setIsLoadingData(false);
				}
			} catch (error) {
				console.error('Error fetching panorama data:', error);
				setIsLoadingData(false);
			}
		};

		fetchAllData();
	}, [panorama_node, allNodesData]);

	// Initialize viewer ONCE with all nodes loaded
	useEffect(() => {
		if (isLoadingData || !allNodesData || !viewerContainer.current || viewer.current) return;
		if (!panorama_node) return;

		// Build links for each node - in GPS mode, just need nodeId
		const buildLinksForNode = (nodeId) => {
			const links = [];
			allNodesData.edges.forEach(edge => {
				let targetNodeId = null;

				if (edge.from_panorama_node_id === nodeId) {
					targetNodeId = edge.to_panorama_node_id;
				} else if (edge.to_panorama_node_id === nodeId) {
					targetNodeId = edge.from_panorama_node_id;
				}

				if (targetNodeId && allNodesData.nodesMap[targetNodeId]) {
					links.push({
						nodeId: targetNodeId,
						name: allNodesData.nodesMap[targetNodeId].name || 'Navigate'
					});
				}
			});

			return links.filter(link => link.nodeId !== nodeId);
		};

		// Build all nodes with GPS coordinates
		const allNodes = [];

		for (let i = 0; i < allNodesData.nodes.length; i++) {
			const node = allNodesData.nodes[i];

			if (!node.panorama_url) continue;

			allNodes.push({
				id: node._id,
				panorama: node?.panorama_url || '',
				name: node.name || 'Location',
				gps: [parseFloat(node.longitude), parseFloat(node.latitude)],
				links: buildLinksForNode(node._id),
				sphereCorrection: {
					pan: `${-(node.pan_offset || 0) + 180}deg`,
					tilt: `${node.tilt_offset || 0}deg`,
					roll: `${node.roll_offset || 0}deg`
				}
			});
		}

		// Initialize the viewer with VirtualTourPlugin in GPS mode
		viewer.current = new Viewer({
			container: viewerContainer.current,
			// Gestures as in Google Street View. The viewer fills the page, so there is nothing
			// underneath for a one-finger drag or a plain scroll to be meant for: one finger looks
			// around (two pinch to zoom), the wheel zooms without Ctrl, and a flick keeps turning
			// briefly after release.
			touchmoveTwoFingers: false,
			mousewheelCtrlKey: false,
			moveInertia: true,
			navbar: false,
			defaultZoomLvl: 0,
			// Our panoramas are always full, uncropped equirectangular captures.
			// PSV's XMP-based panoData auto-detection reads the JPEG as text and
			// can misparse embedded metadata (seen with Insta360 exports), producing
			// a bogus fullWidth/fullHeight that builds a partial sphere with a
			// permanent gap. Trust the actual decoded image dimensions instead.
			adapter: [EquirectangularAdapter, { useXmpData: false }],
			plugins: [
				[VirtualTourPlugin, {
					positionMode: 'gps',
					renderMode: '3d',
					transitionOptions: {
						showLoader: true,
						speed: '20rpm',
						effect: 'fade',
						rotation: true
					}
				}]
			]
		});

		virtualTourPlugin.current = viewer.current.getPlugin(VirtualTourPlugin);

		// Set all nodes at once - this allows seamless navigation without re-rendering
		virtualTourPlugin.current.setNodes(allNodes, panorama_node._id);
		setCurrentNodeId(panorama_node._id);

		// VirtualTourPlugin's async node-loading chain can leave the renderer's
		// needsUpdate flag cleared before the animation loop's first tick consumes
		// it, leaving the canvas on its blank clear color until something else
		// (e.g. a drag) triggers a render. Force one render pass once ready.
		viewer.current.addEventListener('ready', () => {
			viewer.current.needsUpdate();
		}, { once: true });

		// Listen for viewer position updates to rotate minimap compass
		const handlePositionUpdate = (e) => {
			const { yaw } = e.position;
			const yaw_deg = ((yaw * 180 / Math.PI) % 360);
			setViewerYaw(yaw_deg);
		};

		viewer.current.addEventListener('position-updated', handlePositionUpdate);

		const handleNodeChanged = ({ node }) => {
			setCurrentNodeId(node.id);

			// Update URL query parameter to reflect current node
			const currentQuery = GeneralHelper.getParamsFromUrl();
			const queryString = GeneralHelper.objectToQueryString({
				...currentQuery,
				id: node.id
			});
			// `replace` (and keeping `state`) so a tour of ten panoramas is still one entry: back
			// returns to the map, not to the previous panorama.
			history.replace({ pathname: '/street-view', search: `?${queryString}`, state: history.location.state });
		};

		virtualTourPlugin.current.addEventListener('node-changed', handleNodeChanged);

		return () => {
			if (virtualTourPlugin.current) {
				virtualTourPlugin.current.removeEventListener('node-changed', handleNodeChanged);
				virtualTourPlugin.current = null;
			}

			if (viewer.current) {
				viewer.current.removeEventListener('position-updated', handlePositionUpdate);
				viewer.current.destroy();
				viewer.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allNodesData, isLoadingData]);

	// Back belongs to the map the user came from — the floor plan with the 360 graph drawn over it,
	// not the bare plan they'd have to re-enter street view on. Going back rather than pushing also
	// keeps the stack from growing a rung per panorama visited. Only a cold-opened link has no such
	// entry to return to; there we rebuild the closest thing, the node's floor with the graph on.
	const handleGoBack = () => {
		if (openedFromMap.current) {
			history.goBack();
			return;
		}

		if (panorama_node && panorama_node.building_id && panorama_node.building_floor_id) {
			const queryString = GeneralHelper.objectToQueryString({
				id: panorama_node.building_id,
				building_floor_id: panorama_node.building_floor_id,
				street_view: '1'
			});
			history.replace(`/location/id?${queryString}`);
		} else {
			history.replace('/');
		}
	};

	const handleZoomIn = () => {
		if (viewer.current) {
			viewer.current.zoom(viewer.current.getZoomLevel() + 10);
		}
	};

	const handleZoomOut = () => {
		if (viewer.current) {
			viewer.current.zoom(viewer.current.getZoomLevel() - 10);
		}
	};

	const handleFullscreen = () => {
		if (viewer.current) {
			viewer.current.enterFullscreen();
		}
	};

	return (
		<div className="street-view-page">
			<div className="street-view-controls">
				<Button
					size="large"
					onClick={handleGoBack}
					type="default"
					icon={<FontAwesomeIcon icon={faArrowLeft} />}
					style={{
						width: 58,
						height: 58,
						boxShadow: '0 0px 24px -12px #000000AA'
					}}
				/>
			</div>

			{!isLoadingData && viewer.current && (
				<div className="street-view-viewer-controls">
					<Button
						size="large"
						onClick={handleZoomIn}
						type="default"
						icon={<FontAwesomeIcon icon={faPlus} />}
						style={{
							width: 48,
							height: 48,
							marginBottom: 8
						}}
					/>
					<Button
						size="large"
						onClick={handleZoomOut}
						type="default"
						icon={<FontAwesomeIcon icon={faMinus} />}
						style={{
							width: 48,
							height: 48,
							marginBottom: 8
						}}
					/>
					<Button
						size="large"
						onClick={handleFullscreen}
						type="default"
						icon={<FontAwesomeIcon icon={faExpand} />}
						style={{
							width: 48,
							height: 48
						}}
					/>
				</div>
			)}

			{isLoadingData && !nodeUnavailable && (
				<div className="street-view-loading">
					<LoadingOutlined style={{ fontSize: 48, color: '#0088FF' }} />
				</div>
			)}

			{nodeUnavailable && (
				<div className="street-view-unavailable">
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description="This 360° view is no longer available."
					>
						<Button
							type="primary"
							onClick={handleGoBack}
						>
							Back to map
						</Button>
					</Empty>
				</div>
			)}

			<div
				ref={viewerContainer}
				className="street-view-container"
				style={{
					width: '100%',
					height: '100vh',
					display: isLoadingData ? 'none' : 'block'
				}}
			/>

			<Minimap
				allNodesData={allNodesData}
				currentNodeId={currentNodeId}
				viewerYaw={viewerYaw}
				panorama_node={panorama_node}
				isLoadingData={isLoadingData}
				virtualTourPlugin={virtualTourPlugin}
			/>
		</div>
	);
};

export default StreetViewPage;